import React from 'react';
import PropTypes from 'prop-types';
import BaseBox from '../BaseBox';
import cx from 'classnames';
import {createCustomPropType} from '@homebase/common';
import './Text.$BRAND.css.js';

const unsupportedElements = {
  div: 'Box',
};

function customElementValidator(props) {
  const renderedInvalidElement = Object.keys(unsupportedElements).includes(
    props.is
  );
  if (renderedInvalidElement) {
    throw new Error(
      `Text does not support the ${props.is} element, instead use the ${
        unsupportedElements[props.is]
      } component.`
    );
  }
  return null;
}

function Text({
  children,
  is,
  fontStyle,
  size,
  textAlign,
  display,
  testIds,
  m,
  p,
  mb,
  pb,
  mt,
  pt,
  mr,
  pr,
  ml,
  pl,
  mx,
  my,
  px,
  py,
  id,
  color,
  truncatedLines,
}) {
  return (
    <BaseBox
      truncatedLines={truncatedLines}
      testIds={testIds}
      display={display}
      textAlign={textAlign}
      is={is}
      className={cx('pl-Text', {
        [`pl-Text--${fontStyle}`]: !!fontStyle,
        [`pl-Text--${size}`]: !!size,
        [`pl-Text--${color}`]: !!color,
      })}
      m={m}
      p={p}
      mb={mb}
      pb={pb}
      mt={mt}
      pt={pt}
      mr={mr}
      pr={pr}
      ml={ml}
      pl={pl}
      mx={mx}
      my={my}
      px={px}
      py={py}
      data-hb-id="Text"
      id={id}
    >
      {children}
    </BaseBox>
  );
}

Text.propTypes = {
  // The text content
  children: PropTypes.node.isRequired,
  // The element that the Text component will render as
  is: createCustomPropType(customElementValidator),
  // The style of the text
  fontStyle: PropTypes.oneOf(['italic', 'bold']),
  // The size of the text
  size: PropTypes.oneOf(['small']),
  // The alignment of the Text element
  // This is a responsive prop that accepts either an object of { breakpoint: alignment }
  // or just an alignment value for all breakpoints
  textAlign: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['left', 'center', 'right']),
      bp320: PropTypes.oneOf(['left', 'center', 'right']),
      bp480: PropTypes.oneOf(['left', 'center', 'right']),
      bp640: PropTypes.oneOf(['left', 'center', 'right']),
      bp800: PropTypes.oneOf(['left', 'center', 'right']),
      bp960: PropTypes.oneOf(['left', 'center', 'right']),
      bp1120: PropTypes.oneOf(['left', 'center', 'right']),
      bp1280: PropTypes.oneOf(['left', 'center', 'right']),
      bp1440: PropTypes.oneOf(['left', 'center', 'right']),
      bp1600: PropTypes.oneOf(['left', 'center', 'right']),
    }),
    PropTypes.oneOf(['left', 'center', 'right']),
  ]),
  // The display value is the default CSS display of the rendered HTML element.
  // Only use this prop if you need to change the standard display of the element,
  //  - and consider whether using another element makes more sense instead.
  display: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp320: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp480: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp640: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp800: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp960: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1120: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1280: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1440: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1600: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
    }),
    PropTypes.oneOf([
      'none',
      'flex',
      'block',
      'inline',
      'inline-block',
      'inline-flex',
    ]),
  ]),
  // Optional Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
  // The margin, applied to all sides of the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  m: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The padding, applied to all sides of the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  p: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The bottom margin applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  mb: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The bottom padding applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  pb: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The top margin applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  mt: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The top padding applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  pt: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The right margin applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  mr: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The right padding applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  pr: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The left margin applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  ml: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The left padding applied to the Text component
  // This is a responsive prop that accepts either an object of { breakpoint: space }
  // or just a spacing value for all breakpoints
  pl: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The horizontal (aka "x axis") margin applied to the Text element
  // This will apply margin-left and margin-right to the Text
  mx: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The vertical (aka "y axis") margin applied to the Text element
  // This will apply margin-top and margin-bottom to the Text
  my: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The horizontal (aka "x axis") padding applied to the Text element
  // This will apply padding-left and padding-right to the Text
  px: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The vertical (aka "y axis") padding applied to the Text element
  // This will apply padding-top and padding-bottom to the Text
  py: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The id attribute of the text
  id: PropTypes.string,
  // Color variation of the text
  color: PropTypes.oneOf(['error', 'muted', 'sale']),
  // The number of lines that the text content will be truncated at
  truncatedLines: PropTypes.oneOf([null, 1, 2]),
};

Text.defaultProps = {
  is: 'p',
  fontStyle: null,
  size: null,
  textAlign: null,
  display: null,
  testIds: {},
  m: null,
  p: null,
  mb: null,
  pb: null,
  mt: null,
  pt: null,
  mr: null,
  pr: null,
  ml: null,
  pl: null,
  id: null,
  color: null,
  truncatedLines: null,
  py: null,
  my: null,
  px: null,
  mx: null,
};

Text.breakpoints = BaseBox.breakpoints;

export default Text;
